import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { getSaved, fetchListings, createListing, getUserThreads } from "../lib/api";
import { useAuth } from "../lib/auth";
import ListingCard from "../components/ListingCard";
import ProfileTab from "../components/ProfileTab";
import { toast } from "sonner";
import { Plus, Bookmark, Briefcase, MessageSquare, TrendingUp, FileText, User as UserIcon } from "lucide-react";

const COUNTRIES = ["UAE", "Saudi Arabia", "Qatar", "Bahrain", "Oman", "Kuwait", "Nigeria", "Ghana", "Kenya", "South Africa", "Egypt"];
const INDUSTRIES = ["Technology", "Retail", "E-commerce", "Hospitality", "Food & Beverage", "Construction", "Manufacturing", "Healthcare", "Education", "Logistics", "Real Estate", "Agriculture", "Services", "Beauty & Wellness", "Automotive"];

function SellerPanel({ email }) {
  const [showForm, setShowForm] = useState(false);
  const [myListings, setMyListings] = useState([]);
  const [f, setF] = useState({
    business_name: "", industry: "Technology", country: "UAE", city: "",
    revenue_range: "$1M - $2M", profit_range: "$150K - $300K", asking_price: 2500000,
    employees: 25, years_operating: 5, description: "", growth_opportunities: "",
    listing_type: "sale", image_url: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=800",
    video_url: "",
  });

  useEffect(() => {
    fetchListings().then(d => setMyListings(d.filter(l => l.owner_email === email)));
  }, [email]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...f, asking_price: +f.asking_price, employees: +f.employees, years_operating: +f.years_operating, owner_email: email, video_url: f.video_url || null };
      const created = await createListing(data);
      toast.success("Listing created. Pending verification.");
      setMyListings([created, ...myListings]); setShowForm(false);
    } catch { toast.error("Failed to create listing."); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="dv-display text-2xl mb-1">My Listings</h2>
          <p className="text-slate-500 text-sm">Manage your businesses on the marketplace.</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="dv-btn dv-btn-primary dv-btn-sm" data-testid="new-listing-btn"><Plus size={15} /> New Listing</button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-5 mb-7 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="new-listing-form">
          <input className="dv-input md:col-span-2" placeholder="Business name" required value={f.business_name} onChange={e => setF({ ...f, business_name: e.target.value })} />
          <select className="dv-input" value={f.industry} onChange={e => setF({ ...f, industry: e.target.value })}>{INDUSTRIES.map(i => <option key={i}>{i}</option>)}</select>
          <select className="dv-input" value={f.country} onChange={e => setF({ ...f, country: e.target.value })}>{COUNTRIES.map(c => <option key={c}>{c}</option>)}</select>
          <input className="dv-input" placeholder="City" value={f.city} onChange={e => setF({ ...f, city: e.target.value })} />
          <select className="dv-input" value={f.listing_type} onChange={e => setF({ ...f, listing_type: e.target.value })}>
            <option value="sale">For Sale</option><option value="investment">Seeking Investment</option><option value="partnership">Partnership</option>
          </select>
          <input className="dv-input" placeholder="Revenue range" value={f.revenue_range} onChange={e => setF({ ...f, revenue_range: e.target.value })} />
          <input className="dv-input" placeholder="Profit range" value={f.profit_range} onChange={e => setF({ ...f, profit_range: e.target.value })} />
          <input type="number" className="dv-input" placeholder="Asking price (USD)" value={f.asking_price} onChange={e => setF({ ...f, asking_price: e.target.value })} />
          <input type="number" className="dv-input" placeholder="Employees" value={f.employees} onChange={e => setF({ ...f, employees: e.target.value })} />
          <input type="number" className="dv-input" placeholder="Years operating" value={f.years_operating} onChange={e => setF({ ...f, years_operating: e.target.value })} />
          <input className="dv-input md:col-span-2" placeholder="Pitch video URL (MP4 or YouTube — optional)" value={f.video_url} onChange={e => setF({ ...f, video_url: e.target.value })} />
          <textarea className="dv-input is-textarea md:col-span-2" rows="3" placeholder="Business description" required value={f.description} onChange={e => setF({ ...f, description: e.target.value })} />
          <textarea className="dv-input is-textarea md:col-span-2" rows="2" placeholder="Growth opportunities" required value={f.growth_opportunities} onChange={e => setF({ ...f, growth_opportunities: e.target.value })} />
          <button type="submit" className="dv-btn dv-btn-primary md:col-span-2" data-testid="submit-listing-btn">Submit Listing</button>
        </form>
      )}

      {myListings.length === 0 ? (
        <div className="bg-slate-50 rounded-2xl p-12 text-center text-slate-400">No listings yet. Click "New Listing" to add one.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{myListings.map(l => <ListingCard key={l.id} listing={l} />)}</div>
      )}
    </div>
  );
}

function SavedPanel({ email }) {
  const [items, setItems] = useState([]);
  useEffect(() => { if (email) getSaved(email).then(setItems); }, [email]);
  return (
    <div>
      <h2 className="dv-display text-2xl mb-1">Saved Opportunities</h2>
      <p className="text-slate-500 text-sm mb-6">Opportunities you've bookmarked.</p>
      {items.length === 0 ? (
        <div className="bg-slate-50 rounded-2xl p-12 text-center text-slate-400">No saved items. <Link to="/marketplace" className="text-blue-600 font-medium">Browse marketplace</Link>.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{items.map(l => <ListingCard key={l.id} listing={l} />)}</div>
      )}
    </div>
  );
}

function MessagesPanel({ email }) {
  const [threads, setThreads] = useState([]);
  useEffect(() => { if (email) getUserThreads(email).then(setThreads); }, [email]);
  return (
    <div>
      <h2 className="dv-display text-2xl mb-1">Messages</h2>
      <p className="text-slate-500 text-sm mb-6">Conversations with buyers, sellers, and investors.</p>
      {threads.length === 0 ? (
        <div className="bg-slate-50 rounded-2xl p-12 text-center text-slate-400">No conversations yet.</div>
      ) : (
        <div className="space-y-3">{threads.map(t => (
          <div key={t.thread_id} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex justify-between items-baseline mb-1">
              <span className="font-semibold text-blue-600 text-sm">{t.participants.filter(p => p !== email).join(", ") || "—"}</span>
              <span className="text-xs text-slate-400">{new Date(t.last_message.created_at).toLocaleDateString()}</span>
            </div>
            <p className="text-sm text-slate-700">{t.last_message.content}</p>
          </div>
        ))}</div>
      )}
    </div>
  );
}

function PipelinePanel() {
  const stages = ["Sourced", "Initial Review", "NDA Signed", "Due Diligence", "Term Sheet", "Closed"];
  return (
    <div>
      <h2 className="dv-display text-2xl mb-1">Deal Pipeline</h2>
      <p className="text-slate-500 text-sm mb-6">Track opportunities through stages.</p>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        {stages.map((s, i) => (
          <div key={s} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1.5">Stage {i + 1}</div>
            <div className="font-bold text-base text-slate-900 mb-2">{s}</div>
            <div className="text-xs text-slate-400">— deals</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [params] = useSearchParams();
  const initialRole = params.get("role") || "investor";
  const { user } = useAuth();
  const email = user?.email || "";

  return (
    <div data-testid="dashboard-page" className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      <div className="mb-8">
        <h1 className="dv-display text-3xl md:text-4xl">Welcome, {user?.full_name || user?.email?.split("@")[0]}</h1>
        <p className="text-slate-500 mt-1">Signed in as <span className="font-semibold text-blue-600">{email}</span></p>
      </div>

      <Tabs defaultValue={initialRole === "seller" || initialRole === "founder" ? "listings" : "saved"} className="w-full" data-testid="dashboard-tabs">
        <TabsList className="bg-transparent border-b border-slate-200 w-full justify-start rounded-none p-0 mb-8 h-auto flex-wrap">
          <TabsTrigger value="saved" className="bg-transparent rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:shadow-none px-4 py-3 font-medium"><Bookmark size={14} className="mr-2" /> Saved</TabsTrigger>
          <TabsTrigger value="listings" className="bg-transparent rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:shadow-none px-4 py-3 font-medium"><Briefcase size={14} className="mr-2" /> Listings</TabsTrigger>
          <TabsTrigger value="pipeline" className="bg-transparent rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:shadow-none px-4 py-3 font-medium"><TrendingUp size={14} className="mr-2" /> Pipeline</TabsTrigger>
          <TabsTrigger value="messages" className="bg-transparent rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:shadow-none px-4 py-3 font-medium"><MessageSquare size={14} className="mr-2" /> Messages</TabsTrigger>
          <TabsTrigger value="profile" className="bg-transparent rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:shadow-none px-4 py-3 font-medium"><UserIcon size={14} className="mr-2" /> Profile</TabsTrigger>
          <TabsTrigger value="diligence" className="bg-transparent rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:shadow-none px-4 py-3 font-medium"><FileText size={14} className="mr-2" /> Due Diligence</TabsTrigger>
        </TabsList>
        <TabsContent value="saved"><SavedPanel email={email} /></TabsContent>
        <TabsContent value="listings"><SellerPanel email={email} /></TabsContent>
        <TabsContent value="pipeline"><PipelinePanel /></TabsContent>
        <TabsContent value="messages"><MessagesPanel email={email} /></TabsContent>
        <TabsContent value="profile"><ProfileTab /></TabsContent>
        <TabsContent value="diligence">
          <h2 className="dv-display text-2xl mb-1">Due Diligence Room</h2>
          <p className="text-slate-500 text-sm mb-6">Secure document exchange — available once an NDA is signed.</p>
          <div className="bg-slate-50 rounded-2xl p-12 text-center text-slate-400">No active rooms yet.</div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
